import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useNoIndex } from '@/hooks/useSEO';

const OrderSuccessPage = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || '';
  const shortRef = sessionId ? sessionId.replace('cs_live_', '').replace('cs_test_', '').slice(0, 12).toUpperCase() : null;
  const { clearCart } = useCart();
  const [visible, setVisible] = useState(false);
  const [checkDone, setCheckDone] = useState(false);
  const [ringDone, setRingDone] = useState(false);

  useEffect(() => {
    clearCart();
    const t1 = setTimeout(() => setVisible(true), 100);
    const t2 = setTimeout(() => setCheckDone(true), 900);
    const t3 = setTimeout(() => setRingDone(true), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [clearCart]);

  const steps = [
    {
      icon: 'ri-mail-check-line',
      color: 'from-violet-600/30 to-fuchsia-600/30 border-violet-500/25',
      iconColor: 'text-violet-400',
      title: 'Bevestiging per e-mail',
      desc: 'Je ontvangt binnen een paar minuten een betalingsbevestiging van Stripe op je e-mailadres.',
    },
    {
      icon: 'ri-box-3-line',
      color: 'from-fuchsia-600/20 to-violet-600/20 border-fuchsia-500/20',
      iconColor: 'text-fuchsia-400',
      title: 'Verwerking &amp; verzending',
      desc: 'Jouw bestelling wordt zorgvuldig verpakt en zo snel mogelijk verstuurd — wereldwijd, discreet en persoonlijk.',
    },
    {
      icon: 'ri-whatsapp-line',
      color: 'from-emerald-600/20 to-teal-600/20 border-emerald-500/20',
      iconColor: 'text-emerald-400',
      title: 'Persoonlijk contact',
      desc: 'Heb je vragen? We zijn altijd bereikbaar via WhatsApp voor updates over je bestelling.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#050010] flex flex-col">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/64d8f16be4dd66c881723fc151feb4e4.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/70 via-[#050010]/50 to-[#050010]" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20 pt-28">
        <div
          className={`w-full max-w-2xl transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Check animation */}
          <div className="flex justify-center mb-10">
            <div className="relative">
              {/* Outer glow ring */}
              <div
                className={`absolute inset-0 rounded-full transition-all duration-700 ${
                  ringDone
                    ? 'scale-150 opacity-0'
                    : 'scale-100 opacity-100 bg-violet-500/20'
                }`}
              />
              {/* Ring pulse */}
              <div className="absolute inset-0 rounded-full animate-ping bg-violet-500/10" />
              {/* Main circle */}
              <div className="relative w-28 h-28 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-[0_0_60px_rgba(139,92,246,0.5)]">
                <i
                  className={`ri-check-line text-white text-5xl transition-all duration-500 ${
                    checkDone ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-4">
              ✦ Betaling Geslaagd ✦
            </p>
            <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
              Dank voor je{' '}
              <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
                bestelling!
              </span>
            </h1>
            <p className="text-white/50 text-base max-w-md mx-auto leading-relaxed">
              Jouw betaling is bevestigd. We gaan direct aan de slag met het verwerken van je bestelling.
            </p>

            {/* Order ref */}
            {shortRef && (
              <div className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-white/5 border border-violet-500/20 rounded-full">
                <i className="ri-receipt-line text-violet-400 text-sm" />
                <span className="text-white/40 text-sm">Referentie:</span>
                <span className="text-white font-mono font-bold text-sm tracking-wider">#{shortRef}</span>
              </div>
            )}
          </div>

          {/* Next steps */}
          <div className="space-y-4 mb-10">
            <p className="text-white/30 text-xs uppercase tracking-widest font-semibold text-center mb-5">
              Wat gebeurt er nu?
            </p>
            {steps.map((step, i) => (
              <div
                key={i}
                className={`flex items-start gap-5 p-5 rounded-2xl bg-gradient-to-r border transition-all duration-500 ${step.color}`}
                style={{ transitionDelay: `${i * 100 + 400}ms` }}
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-white/8 shrink-0 ${step.iconColor}`}>
                  <i className={`${step.icon} text-xl`} />
                </div>
                <div>
                  <h3
                    className="text-white font-semibold text-sm mb-1"
                    dangerouslySetInnerHTML={{ __html: step.title }}
                  />
                  <p className="text-white/45 text-xs leading-relaxed">{step.desc}</p>
                </div>
                <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-white/8 text-white/25 text-xs font-bold mt-0.5">
                  {i + 1}
                </div>
              </div>
            ))}
          </div>

          {/* Shipping badge */}
          <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
            {[
              { icon: 'ri-global-line', text: 'Wereldwijde Verzending' },
              { icon: 'ri-shield-check-line', text: 'Veilig Betaald via Stripe' },
              { icon: 'ri-box-1-line', text: 'Discreet Verpakt' },
            ].map(badge => (
              <div
                key={badge.text}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/8 rounded-full"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${badge.icon} text-violet-400 text-sm`} />
                </div>
                <span className="text-white/50 text-xs font-medium whitespace-nowrap">{badge.text}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/orders"
              className="flex items-center justify-center gap-2.5 px-8 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer"
            >
              <i className="ri-receipt-line text-base" />
              Mijn Bestellingen Bekijken
            </Link>
            <Link
              to="/shop"
              className="flex items-center justify-center gap-2.5 px-8 py-4 border border-violet-500/30 text-violet-400 font-semibold rounded-xl whitespace-nowrap hover:bg-violet-500/10 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-line text-base" />
              Verder Winkelen
            </Link>
          </div>

          {/* Help */}
          <div className="mt-10 text-center">
            <p className="text-white/25 text-sm">
              Vragen over je bestelling?{' '}
              <Link to="/contact" className="text-violet-400 hover:text-violet-300 transition-colors underline underline-offset-2">
                Neem contact op
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
